import * as Enumerable from 'linq';
import { DateTime } from 'luxon';
import { ComplianceDocument, ComplianceDocumentStatus, ComplianceDocumentSummary } from '../entities/compliance/compliance.document';
import { IndividualComplianceRule, IndividualComplianceRuleApplyTo } from '../entities/compliance/individual.compliance.rule';
import { DateUtility } from './date.utility';
import { Contact, ContactSummary } from '../entities/contact/contact';

export class IndividualComplianceUtility {

    public static identifyMissingCompliance(rules: IndividualComplianceRule[], compliance: ComplianceDocumentSummary[]): ComplianceDocumentSummary[] {

        const complianceGroup = Enumerable
            .from(compliance)
            .groupBy(x => x.definitionId)
            .toDictionary(x => x.key(), x => x.toArray());

        const missing = [] as ComplianceDocumentSummary[];

        Enumerable.from(rules)
            .forEach(rule => {
                const definitionId = rule.definition._id;
                let isMissing = !complianceGroup.contains(definitionId);

                const complianceForRule = complianceGroup.get(definitionId);
                if (complianceForRule?.every(x => x.active === false)) isMissing = true;

                if (isMissing) {
                    missing.push({
                        _id: definitionId,
                        definitionId: definitionId,
                        status: ComplianceDocumentStatus.Missing,
                        category: rule.definition.category,
                        name: rule.definition.name,
                        end: null,
                        start: null,
                        number: null,
                        definition: rule.definition,
                        blockAssignment: rule.blockAssignment
                    } as ComplianceDocumentSummary);
                } else {
                    complianceGroup
                        .get(definitionId)
                        .forEach(x => {
                            x.blockAssignment = rule.blockAssignment;
                        });
                }
            });

        const all = Enumerable
            .from([...compliance, ...missing])
            .orderBy(x => x.category)
            .thenBy(x => x.name)
            .toArray();

        return all;

    }

    public static calculateComplianceExpiry(rules: IndividualComplianceRule[], compliance: ComplianceDocumentSummary[]): Date {

        const complianceSafe = compliance.filter(x => x.active !== false);

        const complianceGroup = Enumerable
            .from(complianceSafe)
            .groupBy(x => x.definitionId)
            .toDictionary(x => x.key(), x => x.toArray());

        const expiryDates: Date[] = [];

        const minDate = new Date(1970, 1, 1);
        const maxDate = new Date(2100, 12, 31);

        Enumerable.from(rules)
            .forEach(rule => {

                const complianceForRule = complianceGroup.get(rule.definition._id);

                if (rule.definition.end?.enable) {
                    Enumerable
                        .from(complianceForRule)
                        .forEach(item => {

                            switch (item.status) {
                                case ComplianceDocumentStatus.Missing:
                                    expiryDates.push(rule.blockAssignment ? minDate : maxDate);
                                    return;

                                case ComplianceDocumentStatus.Inactive:
                                    expiryDates.push(maxDate);
                                    return;

                                case ComplianceDocumentStatus.Valid:
                                case ComplianceDocumentStatus.ExpiresSoon:
                                case ComplianceDocumentStatus.Expired:
                                    const endDate = new Date(item.end.value);
                                    const endWithGrace = new Date(endDate);
                                    endWithGrace.setDate(endDate.getDate() + rule.gracePeriodDays);
                                    expiryDates.push(rule.blockAssignment ? endWithGrace : maxDate);
                                    // expiryDates.push(endWithGrace);
                                    return;

                                default:
                                    expiryDates.push(maxDate);
                                    return;
                            }

                        });
                } else {
                    Enumerable
                        .from(complianceForRule)
                        .forEach(item => {

                            switch (item.status) {
                                case ComplianceDocumentStatus.Missing:
                                    expiryDates.push(rule.blockAssignment ? minDate : maxDate);
                                    return;

                                default:
                                    expiryDates.push(maxDate);
                                    return;
                            }
                        });
                }

            });

        const time = expiryDates.length
            ? Enumerable.from(expiryDates).min(x => x.getTime())
            : maxDate.getTime();

        const complianceExpiry = new Date(time);

        return complianceExpiry;

    }

    public static getComplianceSummary(item: ComplianceDocument): ComplianceDocumentSummary {

        const emptyDefinition = {
            category: 'Not Provided',
            name: 'Invalid',
            start: { enable: false, label: null as string },
            end: { enable: false, label: null as string },
            number: { enable: false, label: null as string }
        };

        const definition = item?.definition
            ? item?.definition
            : emptyDefinition;

        const realStatus = IndividualComplianceUtility.getComplianceStatus(item);
        let status = realStatus;

        if (item?.disabled)
            status = ComplianceDocumentStatus.Inactive;

        const summary = {
            category: definition?.category,
            name: definition?.name,
            start: definition?.start?.enable
                ? { label: definition?.start?.label, value: item?.start }
                : null,
            end: definition?.end?.enable
                ? { label: definition?.end?.label, value: item?.end }
                : null,
            number: definition?.number?.enable
                ? { label: definition?.number?.label, value: item?.number }
                : null,
            status: status,
            realStatus: realStatus,
            _id: item?._id,
            definitionId: item?.definition?._id,
            owner: item?.owner,
            active: item?.active,
        } as ComplianceDocumentSummary;

        return summary;
    }

    public static getComplianceStatus(compliance: ComplianceDocument): ComplianceDocumentStatus {

        if (!compliance) return ComplianceDocumentStatus.Expired;

        const today = DateTime.utc().startOf('day');
        const warningDate = today.plus({ days: 60 });

        const end = compliance.end
            ? DateUtility.getDateTime(compliance.end)
            : null;

        switch (true) {

            case !!compliance.supersededBy: {
                return ComplianceDocumentStatus.Superseded;
            }

            case compliance.end && end < today: {
                return ComplianceDocumentStatus.Expired;
            }

            case compliance.end && end <= warningDate: {
                return ComplianceDocumentStatus.ExpiresSoon;
            }

            default:
                return ComplianceDocumentStatus.Valid;
        }


    }

    public static hasNewCompliance(user: Partial<Contact>): boolean {
        if (!user || !user.newComplianceAdded) return false;

        const last7Days = new Date(new Date().getTime() - (8 * 24 * 60 * 60 * 1000));
        const newAdded = new Date(user.newComplianceAdded);

        return newAdded >= last7Days;
    }

    /** 
     * Evaluates compliance status for a list of contacts and determines if they should be disabled.
     * 
     * This function:
     * 1. Groups compliance documents by owner ID for efficient lookup
     * 2. Groups compliance rules by definition ID for efficient lookup
     * 3. For each contact:
     *    a. Finds existing compliance documents for the contact
     *    b. Identifies which rules apply to the documents the contact already has
     *    c. Determines all rules applicable to the contact based on their labels/types
     *    d. Identifies which required documents are missing by comparing applicable rules 
     *       with existing documents
     *    e. Combines rules for existing documents and potentially missing documents
     *    f. Uses identifyMissingCompliance to create a complete compliance status report
     *    g. Marks the contact as disabled if they have missing or expired documents 
     *       with blockAssignment=true
     * 
     * @param rules - All possible compliance rules in the system
     * @param compliance - All compliance documents for all contacts
     * @param contacts - List of contacts to evaluate
     * @returns The same contacts array with disabled property updated
     */
    public static identifyComplianceStatusForContacts(rules: IndividualComplianceRule[], compliance: ComplianceDocumentSummary[], contacts: ContactSummary[]): ContactSummary[] {
        if (!rules?.length) {
            rules = [];
        }

        const complianceGroupByOwner = Enumerable.from(compliance ?? []).groupBy(x => x.owner._id).toDictionary(x => x.key(), x => x.toArray());
        const ruleGroupByDefinition = Enumerable.from(rules).where(e => !!e.definition?._id).groupBy(x => x.definition._id).toDictionary(x => x.key(), x => x.toArray());

        for (const contact of contacts) {

            contact.disabled = false;
            const complianceFound = complianceGroupByOwner.get(contact._id) || [];

            const tempRules: IndividualComplianceRule[] = [];

            for (const item of complianceFound) {
                const ruleItems = ruleGroupByDefinition.get(item.definitionId)
                if (ruleItems?.length) {
                    const validRules = this.filterRules(ruleItems, contact);
                    tempRules.push(...validRules);
                }
            }

            const applicableRules = this.filterRules(rules, contact);
            const mayMissingDocsRule = new Map<string, IndividualComplianceRule>(applicableRules.map(x => [x._id, x]));

            for (const rule of tempRules) {
                if (mayMissingDocsRule.has(rule._id)) {
                    mayMissingDocsRule.delete(rule._id);
                }
            }

            tempRules.push(...mayMissingDocsRule.values());

            const identifiedItems = this.identifyMissingCompliance(tempRules, complianceFound);

            contact.disabled = identifiedItems.some(x => x.blockAssignment && [
                ComplianceDocumentStatus.Expired,
                ComplianceDocumentStatus.Missing,
                ComplianceDocumentStatus.Inactive,
            ].includes(x.status));
        }

        return contacts;
    }

    private static filterRules(rules: IndividualComplianceRule[], contact: ContactSummary): IndividualComplianceRule[] {
        const valid: IndividualComplianceRule[] = [];
        const contactLabelMap = Enumerable.from(contact.labels ?? []).toDictionary(x => x._id, x => x);

        for (const rule of rules) {
            if (!rule.applyToRestrictions?.length) {
                continue;
            }

            if (rule.applyTo === IndividualComplianceRuleApplyTo.UserLabels) {
                if (!contact.labels?.length) {
                    continue;
                }

                const found = rule.applyToRestrictions.some(l => contactLabelMap.contains(l._id));
                if (found) {
                    valid.push(rule);
                }

                continue;
            }

            if (rule.applyTo === IndividualComplianceRuleApplyTo.UserTypes) {
                const found = rule.applyToRestrictions?.some(l => l._id === contact.type);
                if (found) {
                    valid.push(rule);
                }
            }
        }

        return valid;
    }
}
